import { isValidEmail } from "helpers/email";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { handleChangePhoneWithMask } from "pages/CreateOrder/utils/mask";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetReconciliationQuery } from "store/queries/reconciliationConfig";

import { formValidationConstants } from "../../helpers/formValidations";

export function PaymentAndRemit() {
  const [expanded, setExpanded] = useState(false);
  const { data: reconciliationConfig } = useGetReconciliationQuery();

  const {
    control,
    trigger,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<any>();

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  useEffect(() => {
    if (reconciliationConfig?.data && !getValues("reportType")) {
      const standard = reconciliationConfig.data.find(
        (aType: any) => aType.type?.toLowerCase() === "standard"
      );
      if (standard) {
        setValue("reportType", standard.id);
      }
    }
  }, [reconciliationConfig, getValues, setValue]);

  const reportTypeDropdown = useMemo(() => {
    return (
      reconciliationConfig?.data.map((aType: any) => ({
        name: aType.type,
        value: aType.id,
      })) || []
    );
  }, [reconciliationConfig]);
  return (
    <div className="block bg-white border-round-xl border-1 border-gray-300 p-4">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Payment and Remit Details
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="formgrid grid mt-4">
          <div className="field col-12 md:col-6">
            <Label htmlFor="contactPhone">Contact Phone</Label>
            <Controller
              name="contactPhone"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="contactPhone"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter phone number here"
                  aria-labelledby="contactPhone"
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "contactPhone",
                      trigger,
                    });
                  }}
                  onChange={(e) => {
                    handleChangePhoneWithMask(e, onChange);
                  }}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="reportType">Report Type</Label>
            <Controller
              data-testid="reportType"
              name="reportType"
              control={control}
              rules={{
                required:
                  formValidationConstants.PAYMENT_AND_REMIT_DETAILS.REPORT_TYPE,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <Dropdown
                    id="reportType"
                    data-testid="reportType"
                    value={value}
                    onChange={onChange}
                    options={reportTypeDropdown}
                    optionLabel="name"
                    placeholder="Select"
                    className="w-full"
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "reportType",
                        trigger,
                      });
                    }}
                  />
                  {getFormErrorMessage("reportType", errors)}
                </>
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="unitHospitalNumber">Unit hospital number</Label>
            <Controller
              name="unitHospitalNumber"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="unitHospitalNumber"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter unit hospital number here"
                  aria-labelledby="unitHospitalNumber"
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "unitHospitalNumber",
                      trigger,
                    });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="connectAccountId">Connect Account ID</Label>
            <Controller
              name="connectAccountId"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="connectAccountId"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter Connect Account ID"
                  aria-labelledby="connectAccountId"
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "connectAccountId",
                      trigger,
                    });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="finixMerchantId">Finix Merchant Id</Label>
            <Controller
              name="finixMerchantId"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="finixMerchantId"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter Finix merchant id here"
                  aria-labelledby="finixMerchantId"
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "finixMerchantId",
                      trigger,
                    });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="emailToReceiveReports">
              Email to receive reports
            </Label>
            <Controller
              name="emailToReceiveReports"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <Chips
                    id="emailToReceiveReports"
                    name="emailToReceiveReports"
                    aria-labelledby="emailToReceiveReports"
                    value={value}
                    onChange={onChange}
                    className="w-full"
                    placeholder="Enter email to receive reports here"
                    onAdd={(event) => {
                      const isValid = isValidEmail(event.value);
                      return isValid;
                    }}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "emailToReceiveReports",
                        trigger,
                      });
                    }}
                  />
                  {getFormErrorMessage("emailToReceiveReports", errors)}
                </>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
